/**
 * Hydrate slim admin-list rows into full bookings before the detail modal
 * opens.
 *
 * The paged admin list (src/api/adminBookingsPager.js) asks the server for
 * slim rows: the raw search-offer blob, `flight_data.bookingOption`, was 47%
 * of a 9 MB response nothing in the list reads. Rows that lost it arrive
 * marked `_slim: true`. The detail modal and the editors it launches do read
 * it (fare name, baggage allowance, change eligibility) and AdminEditModal
 * round-trips the whole `flight_data`, so those must always work on the full
 * row. This hook is that boundary: given the `trip` array the modal receives,
 * it fetches the full rows for any slim members and returns the array to
 * render plus whether it is ready.
 *
 * Contract:
 *   - No slim rows (the customer dashboard, UserDetails): pass-through, ready
 *     at once, nothing fetched, same array reference.
 *   - Slim rows: `ready` is false until the fetch resolves; the modal stays
 *     closed rather than briefly showing a row it might let someone edit.
 *   - Re-hydration of the same bookings (the dashboard reloads the list after
 *     an edit and re-selects the trip): the previous full rows stay on screen
 *     while the fresh ones load, so the dialog does not blink shut.
 *   - Fetch failure: fall back to the slim rows and warn. The server-side
 *     write guard (preserveOfferBlob) makes that safe; the fare details that
 *     come from the blob simply show their fallbacks.
 */
import { useEffect, useState } from 'react';

export function needsHydration(trip) {
  return Array.isArray(trip) && trip.some((row) => row && row._slim === true);
}

function tripKey(trip) {
  if (!Array.isArray(trip)) return '';
  return trip
    .map((row) => `${row?.id ?? ''}@${row?.updated_at ?? row?.updatedAt ?? ''}${row?._slim === true ? '~' : ''}`)
    .join('|');
}

function sameIds(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((row, i) => String(row?.id) === String(b[i]?.id));
}

/**
 * @param {object[]|null} trip  Rows as handed to FlightDetailsModal.
 * @param {object} opts
 * @param {(ids: string[]) => Promise<object[]>} opts.fetchFull  Resolves full rows for the ids.
 * @param {boolean} [opts.enabled=true]  False while the modal is closed: nothing is fetched.
 * @returns {{ trip: object[]|null, ready: boolean }}
 */
export function useHydratedTrip(trip, { fetchFull, enabled = true } = {}) {
  const key = tripKey(trip);
  const slim = enabled && needsHydration(trip);
  const [state, setState] = useState({ key: null, rows: null });

  useEffect(() => {
    if (!slim) return undefined;
    let cancelled = false;
    const ids = trip.filter((row) => row && row._slim === true).map((row) => row.id);
    // Call synchronously so a caller can observe the request the moment the
    // rows change; a throw becomes a rejection like any other failure.
    let pending;
    try { pending = Promise.resolve(fetchFull(ids)); } catch (err) { pending = Promise.reject(err); }
    pending
      .then((full) => {
        if (cancelled) return;
        const byId = new Map((Array.isArray(full) ? full : []).map((row) => [String(row.id), row]));
        setState({ key, rows: trip.map((row) => (row && row._slim === true && byId.get(String(row.id))) || row) });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[FlightDetailsModal] Could not load the full booking; showing the list row.', err?.message || err);
        setState({ key, rows: trip });
      });
    return () => { cancelled = true; };
    // `key` is the content identity of `trip`; a new array with the same rows
    // must not refetch, and a changed row must.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, slim]);

  if (!slim) return { trip, ready: true };
  if (state.rows && state.key === key) return { trip: state.rows, ready: true };
  if (state.rows && sameIds(state.rows, trip)) return { trip: state.rows, ready: true };
  return { trip, ready: false };
}
